/**
 * Challenges Data Hook
 * Wraps gamification store with a cleaner interface for the challenges page
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGamificationStore } from '../../../../stores/gamification-store';
import type {
  UseChallengesDataReturn,
  SharePlatformId,
  LevelUpData,
  Badge,
  EarnedBadge,
  ChallengesHeroStats,
  LevelInfo,
} from '../types';
import { getLevelInfo, getShareChallenges } from '../utils/helpers';
import { BADGES, MAX_SHARES_PER_DAY } from '../utils/constants';

export function useChallengesData(): UseChallengesDataReturn {
  const store = useGamificationStore();
  const [showLevelUpModal, setShowLevelUpModal] = useState(false);
  const [levelUpData, setLevelUpData] = useState<LevelUpData | null>(null);
  const [previousLevel, setPreviousLevel] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Derive sharesToday from store's shareTracker
  const today = new Date().toISOString().split('T')[0];
  const sharesToday = store.shareTracker.date === today
    ? store.shareTracker.platformsShared.length
    : 0;

  // Sync from backend on mount
  useEffect(() => {
    const sync = async () => {
      try {
        setIsLoading(true);
        await store.syncFromBackend();
      } catch (err) {
        console.error('Failed to sync gamification data:', err);
        setError('Failed to load challenges data');
      } finally {
        setIsLoading(false);
      }
    };
    sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track level changes for level-up modal
  useEffect(() => {
    if (previousLevel !== null && store.level > previousLevel) {
      const newLevelInfo = getLevelInfo(store.xp);
      setLevelUpData({
        newLevel: store.level,
        title: newLevelInfo.title,
        tier: newLevelInfo.tier,
        rewards: newLevelInfo.rewards,
      });
      setShowLevelUpModal(true);
    }
    setPreviousLevel(store.level);
  }, [store.level, store.xp, previousLevel]);

  // Calculate level info
  const levelInfo: LevelInfo | null = useMemo(() => {
    if (isLoading) return null;
    return getLevelInfo(store.xp);
  }, [store.xp, isLoading]);

  // Calculate hero stats
  const heroStats: ChallengesHeroStats = useMemo(() => ({
    currentLevel: store.level,
    totalXp: store.xp,
    dayStreak: store.streakDays,
    sharesToday,
    maxSharesPerDay: MAX_SHARES_PER_DAY,
  }), [store.level, store.xp, store.streakDays, sharesToday]);

  // Process badges - merge store badges with our badge definitions
  const badges: Badge[] = useMemo(() => {
    return BADGES.map(badge => {
      const earnedBadge = store.badges.find(b => b.id === badge.id);
      return {
        ...badge,
        earnedAt: earnedBadge?.earnedAt ?? null,
      };
    });
  }, [store.badges]);

  const earnedBadges: EarnedBadge[] = useMemo(() => {
    return badges.filter((b): b is EarnedBadge => b.earnedAt != null);
  }, [badges]);

  const lockedBadges: Badge[] = useMemo(() => {
    return badges.filter(b => !b.earnedAt);
  }, [badges]);

  // Share challenges
  const shareXpRewards = useMemo(() => {
    return getShareChallenges(sharesToday);
  }, [sharesToday]);

  const canShare = sharesToday < MAX_SHARES_PER_DAY;

  // Record share action
  const recordShare = useCallback(async (platform: SharePlatformId) => {
    if (!canShare) return;

    try {
      await store.recordShare(platform);
    } catch (err) {
      console.error('Failed to record share:', err);
      throw err;
    }
  }, [store, canShare]);

  // Dismiss level up modal
  const dismissLevelUpModal = useCallback(() => {
    setShowLevelUpModal(false);
    setLevelUpData(null);
  }, []);

  return {
    // Stats
    stats: {
      xp: store.xp,
      level: store.level,
      streak: store.streakDays,
      sharesToday,
      lastShareDate: store.shareTracker.date || null,
      badges: badges,
      totalScans: store.totalAsIsScans || 0,
      totalReferrals: store.referralCount || 0,
    },
    levelInfo,
    heroStats,

    // Badges
    badges,
    earnedBadges,
    lockedBadges,

    // Shares
    sharesToday,
    canShare,
    shareXpRewards,

    // Actions
    recordShare,

    // State
    isLoading,
    error,

    // Level up
    showLevelUpModal,
    levelUpData,
    dismissLevelUpModal,
  };
}
